// Shared catalogue of zone-arrangement templates. Each template fixes a
// CSS-grid (cols x rows) plus the rectangle each zone occupies inside it
// (col_start / col_span / row_start / row_span — 1-indexed so they map
// straight onto `grid-column` / `grid-row`). Layouts can additionally
// override column / row sizing via `column_weights` / `row_weights`,
// which feeds into `grid-template-columns/rows` as `fr` units so the
// designer can drag-resize zones along their borders.
//
// The backend validates the template key — keep `TEMPLATES` here in sync
// with `TEMPLATES` in backend/app/schemas/signage_layout.py.

import type React from 'react'

export type LayoutTemplate =
  | 'default'
  | 'main'
  | 'main-footer'
  | 'main-left-bar'
  | 'main-right-bar'
  | 'split-vertical'
  | 'main-lower-pane'
  | 'split-horizontal'
  | 'main-upper-pane'
  | 'four-grid'
  | 'six-grid'
  | 'eight-grid'
  | 'nine-grid'
  | 'twelve-grid'
  | 'sixteen-grid'
  | 'twenty-grid'
  | 'main-four-corners'
  | 'main-six-sides'
  | 'main-eight-sides'

export type ZoneSlot = {
  // 1-indexed grid placement. Spans default to 1.
  col: number
  row: number
  colSpan?: number
  rowSpan?: number
}

export type TemplateDef = {
  key: LayoutTemplate
  label: string
  cols: number
  rows: number
  // Default per-track weights used if the layout doesn't override them.
  // Length must match `cols` / `rows`.
  defaultColWeights: number[]
  defaultRowWeights: number[]
  zones: ZoneSlot[]
}

// Convenience: a uniform 1-weight track list of length `n`.
const ones = (n: number) => Array.from({ length: n }, () => 1)

// Convenience: build N single-cell zones tiling a `cols × rows` grid in
// row-major order. Used for the plain N-grid templates.
function tileZones(cols: number, rows: number): ZoneSlot[] {
  const out: ZoneSlot[] = []
  for (let r = 1; r <= rows; r++) {
    for (let c = 1; c <= cols; c++) out.push({ col: c, row: r })
  }
  return out
}

export const TEMPLATES: TemplateDef[] = [
  // Plain default — a 2×2 four-zone grid. Same arrangement as Four Grid;
  // the dedicated entry is here so the picker always has a friendly first
  // option to fall back to.
  {
    key: 'default',
    label: 'Default',
    cols: 2, rows: 2,
    defaultColWeights: ones(2),
    defaultRowWeights: ones(2),
    zones: tileZones(2, 2),
  },

  // A single full-frame zone — the simplest possible layout.
  {
    key: 'main',
    label: 'Main',
    cols: 1, rows: 1,
    defaultColWeights: [1],
    defaultRowWeights: [1],
    zones: [{ col: 1, row: 1 }],
  },

  // Main fills the upper portion of the frame; footer is a thin strip
  // along the bottom for tickers / branding / etc.
  {
    key: 'main-footer',
    label: 'Main + Footer',
    cols: 1, rows: 4,
    defaultColWeights: [1],
    defaultRowWeights: [1, 1, 1, 1],
    zones: [
      { col: 1, row: 1, rowSpan: 3 },
      { col: 1, row: 4 },
    ],
  },

  // Thin vertical strip on the start side + a wide main pane.
  {
    key: 'main-left-bar',
    label: 'Main + Left Bar',
    cols: 4, rows: 1,
    defaultColWeights: [1, 1, 1, 1],
    defaultRowWeights: [1],
    zones: [
      { col: 1, row: 1 },
      { col: 2, row: 1, colSpan: 3 },
    ],
  },

  // Wide main pane + thin vertical strip on the end side.
  {
    key: 'main-right-bar',
    label: 'Main + Right Bar',
    cols: 4, rows: 1,
    defaultColWeights: [1, 1, 1, 1],
    defaultRowWeights: [1],
    zones: [
      { col: 1, row: 1, colSpan: 3 },
      { col: 4, row: 1 },
    ],
  },

  // Two equal columns — classic split-screen.
  {
    key: 'split-vertical',
    label: 'Split Vertically',
    cols: 2, rows: 1,
    defaultColWeights: [1, 1],
    defaultRowWeights: [1],
    zones: [
      { col: 1, row: 1 },
      { col: 2, row: 1 },
    ],
  },

  // Main on top + a smaller pane below — same idea as footer but taller.
  {
    key: 'main-lower-pane',
    label: 'Main + Lower Pane',
    cols: 1, rows: 3,
    defaultColWeights: [1],
    defaultRowWeights: [1, 1, 1],
    zones: [
      { col: 1, row: 1, rowSpan: 2 },
      { col: 1, row: 3 },
    ],
  },

  // Two equal rows stacked vertically.
  {
    key: 'split-horizontal',
    label: 'Split Horizontally',
    cols: 1, rows: 2,
    defaultColWeights: [1],
    defaultRowWeights: [1, 1],
    zones: [
      { col: 1, row: 1 },
      { col: 1, row: 2 },
    ],
  },

  // Small upper pane + main filling the rest.
  {
    key: 'main-upper-pane',
    label: 'Main + Upper Pane',
    cols: 1, rows: 3,
    defaultColWeights: [1],
    defaultRowWeights: [1, 1, 1],
    zones: [
      { col: 1, row: 1 },
      { col: 1, row: 2, rowSpan: 2 },
    ],
  },

  // 2 × 2 grid of equal cells.
  {
    key: 'four-grid',
    label: 'Four Grid',
    cols: 2, rows: 2,
    defaultColWeights: ones(2),
    defaultRowWeights: ones(2),
    zones: tileZones(2, 2),
  },

  // 3 × 2 grid.
  {
    key: 'six-grid',
    label: 'Six Grid',
    cols: 3, rows: 2,
    defaultColWeights: ones(3),
    defaultRowWeights: ones(2),
    zones: tileZones(3, 2),
  },

  // 4 × 2 grid.
  {
    key: 'eight-grid',
    label: 'Eight Grid',
    cols: 4, rows: 2,
    defaultColWeights: ones(4),
    defaultRowWeights: ones(2),
    zones: tileZones(4, 2),
  },

  // 3 × 3 grid.
  {
    key: 'nine-grid',
    label: 'Nine Grid',
    cols: 3, rows: 3,
    defaultColWeights: ones(3),
    defaultRowWeights: ones(3),
    zones: tileZones(3, 3),
  },

  // 4 × 3 grid.
  {
    key: 'twelve-grid',
    label: 'Twelve Grid',
    cols: 4, rows: 3,
    defaultColWeights: ones(4),
    defaultRowWeights: ones(3),
    zones: tileZones(4, 3),
  },

  // 4 × 4 grid.
  {
    key: 'sixteen-grid',
    label: 'Sixteen Grid',
    cols: 4, rows: 4,
    defaultColWeights: ones(4),
    defaultRowWeights: ones(4),
    zones: tileZones(4, 4),
  },

  // 5 × 4 grid.
  {
    key: 'twenty-grid',
    label: 'Twenty Grid',
    cols: 5, rows: 4,
    defaultColWeights: ones(5),
    defaultRowWeights: ones(4),
    zones: tileZones(5, 4),
  },

  // Big main occupying the central row, with four small zones at the
  // outer corners (top-left/right + bottom-left/right).
  {
    key: 'main-four-corners',
    label: 'Main + Four Corners',
    cols: 3, rows: 3,
    defaultColWeights: [1, 6, 1],
    defaultRowWeights: [1, 5, 1],
    zones: [
      // 4 corners come first so they overlay nicely if main were
      // expanded — but in this layout they don't overlap with main.
      { col: 1, row: 1 },                  // top-left corner
      { col: 3, row: 1 },                  // top-right corner
      { col: 1, row: 3 },                  // bottom-left corner
      { col: 3, row: 3 },                  // bottom-right corner
      { col: 1, row: 2, colSpan: 3 },      // main — spans the wide middle row
    ],
  },

  // Big main in the centre with six side strips around it: 3 on top and
  // 3 on bottom.
  {
    key: 'main-six-sides',
    label: 'Main + Six Sides',
    cols: 3, rows: 3,
    defaultColWeights: [1, 1, 1],
    defaultRowWeights: [1, 5, 1],
    zones: [
      { col: 1, row: 1 },                  // top-left
      { col: 2, row: 1 },                  // top-mid
      { col: 3, row: 1 },                  // top-right
      { col: 1, row: 3 },                  // bottom-left
      { col: 2, row: 3 },                  // bottom-mid
      { col: 3, row: 3 },                  // bottom-right
      { col: 1, row: 2, colSpan: 3 },      // main spans middle row
    ],
  },

  // Main occupies the centre cell with eight zones forming a ring on
  // the perimeter (4 corners + 4 edges).
  {
    key: 'main-eight-sides',
    label: 'Main + Eight Sides',
    cols: 3, rows: 3,
    defaultColWeights: [1, 4, 1],
    defaultRowWeights: [1, 4, 1],
    zones: [
      { col: 1, row: 1 },                  // top-left
      { col: 2, row: 1 },                  // top-mid
      { col: 3, row: 1 },                  // top-right
      { col: 1, row: 2 },                  // left-mid
      { col: 3, row: 2 },                  // right-mid
      { col: 1, row: 3 },                  // bottom-left
      { col: 2, row: 3 },                  // bottom-mid
      { col: 3, row: 3 },                  // bottom-right
      { col: 2, row: 2 },                  // main — centre cell
    ],
  },
]

export function getTemplate(key: string): TemplateDef {
  return TEMPLATES.find(t => t.key === key) ?? TEMPLATES[0]
}

// ── Orientation classification + picker visibility ─────────────────────────
// Which screen orientation each template is designed for. Column-heavy splits
// suit landscape (horizontal) screens; row-stacked splits suit portrait
// (vertical) screens; single/square arrangements work on either ('both').
// Drives the Horizontal / Vertical filter in the create/change picker.
export type LayoutOrientation = 'horizontal' | 'vertical' | 'both'

const ORIENTATION: Record<LayoutTemplate, LayoutOrientation> = {
  'default':           'both',
  'main':              'both',
  'main-footer':       'vertical',
  'main-left-bar':     'horizontal',
  'main-right-bar':    'horizontal',
  'split-vertical':    'horizontal',
  'main-lower-pane':   'vertical',
  'split-horizontal':  'vertical',
  'main-upper-pane':   'vertical',
  'four-grid':         'both',
  'six-grid':          'horizontal',
  'eight-grid':        'horizontal',
  'nine-grid':         'both',
  'twelve-grid':       'horizontal',
  'sixteen-grid':      'both',
  'twenty-grid':       'horizontal',
  'main-four-corners': 'both',
  'main-six-sides':    'both',
  'main-eight-sides':  'both',
}

export const templateOrientation = (key: LayoutTemplate): LayoutOrientation =>
  ORIENTATION[key] ?? 'both'

// Templates hidden from the create/change picker: the single-zone 'main'
// (a one-zone "layout" is just direct content — nothing to arrange), the
// corner/side arrangements, and any grid with more than 9 zones (too dense to
// author usefully). The definitions stay in TEMPLATES so existing layouts
// using them still render.
const PICKER_HIDDEN: Set<LayoutTemplate> = new Set([
  'main',
  'main-four-corners', 'main-six-sides', 'main-eight-sides',
  'twelve-grid', 'sixteen-grid', 'twenty-grid',
])

export const pickerTemplates = (): TemplateDef[] =>
  TEMPLATES.filter(t => !PICKER_HIDDEN.has(t.key))

// CSS values for grid-template-cols/rows, honouring per-layout overrides
// when provided. Falls back to the template's `defaultColWeights` / etc.
// Resulting weights are normalised by mapping each entry to `${w}fr`.
export function gridTracks(
  tpl: TemplateDef,
  columnWeights?: number[] | null,
  rowWeights?: number[] | null,
): { gridTemplateColumns: string; gridTemplateRows: string } {
  const cols = columnWeights && columnWeights.length === tpl.cols ? columnWeights : tpl.defaultColWeights
  const rows = rowWeights && rowWeights.length === tpl.rows ? rowWeights : tpl.defaultRowWeights
  return {
    gridTemplateColumns: cols.map(w => `${Math.max(0.05, w)}fr`).join(' '),
    gridTemplateRows:    rows.map(w => `${Math.max(0.05, w)}fr`).join(' '),
  }
}

export function zoneStyle(slot: ZoneSlot): React.CSSProperties {
  return {
    gridColumn: `${slot.col} / span ${slot.colSpan ?? 1}`,
    gridRow:    `${slot.row} / span ${slot.rowSpan ?? 1}`,
  }
}

// Generate default zone IDs/names when the user picks a new template. The
// caller can override `name` later via inline edit on the detail page.
export function buildEmptyZones(key: LayoutTemplate): Array<{
  id: string; name: string;
  media_ids: number[]; link_ids: number[];
  media_durations: Record<number, number>; link_durations: Record<number, number>;
  media_order: Record<number, number>;       link_order:  Record<number, number>;
}> {
  const def = getTemplate(key)
  return def.zones.map((_, i) => ({
    id: `z${i + 1}`,
    name: `Zone ${i + 1}`,
    media_ids: [],
    link_ids: [],
    media_durations: {},
    link_durations: {},
    media_order: {},
    link_order: {},
  }))
}

